import React from "react";

import type { BusinessNatureCategory, CfoSource, ClientId, EntityType, ServiceModule } from "../../domain/types";
import { BUSINESS_NATURE_OPTIONS, coTenantDomain, ENTITY_TYPE_OPTIONS } from "../../domain/constants";
import { checkSubdomain } from "../../data/onboardingApi";
import { getClient, updateClient, useCoClient } from "../../data/store";
import { DecisionCards, DecisionCardOption } from "../../components/DecisionCards";
import { QuestionCard } from "../../components/QuestionCard";
import { SubStepShell } from "../../components/SubStepShell";
import { useSubStepNav } from "../useSubStepNav";

const SERVICE_OPTIONS: DecisionCardOption[] = [
  {
    value: "books",
    title: "Books",
    description: "Zournal keeps the books current from Zoho, in your name.",
    consequence: "Included for every client. Connect the books and Verify setup run below.",
    locked: true,
  },
  {
    value: "compliance",
    title: "Compliance",
    description: "GST and TDS filings, generated from the books or from monthly uploads.",
    consequence: "You can add this later.",
  },
  {
    value: "cfo",
    title: "CFO",
    description: "Management reporting and P&L views for this client.",
    consequence: "You can add this later.",
  },
];

const CFO_SOURCE_OPTIONS: DecisionCardOption[] = [
  {
    value: "connect",
    title: "Yes, connect Zoho Books or Tally",
    description: "We'll set up a live connection in the next step.",
  },
  {
    value: "upload",
    title: "No, upload a trial balance instead",
    description: "You'll upload the client's trial balance in Excel. You can connect books later.",
  },
];

type SubdomainStatus = "idle" | "checking" | "available" | "taken" | "invalid" | "error";

const CHECK_DEBOUNCE_MS = 1500;
const SUBDOMAIN_PLACEHOLDER = "your-client";

/**
 * The display filter, and nothing more: case and spaces are not a judgement
 * call, everything else is the server's to refuse. Stripping illegal
 * characters here is how a pasted "umm.notion.zournalhq.com" used to turn
 * itself into the plausible-looking "ummnotionzournalhqcom" and get saved.
 */
function tidySubdomainInput(value: string): string {
  return value.toLowerCase().replace(/\s+/g, "").slice(0, 63);
}

export function CompanyDetailsStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "company-details");

  // The subdomain follows the company name (autogenerated) until the CA types
  // their own — then it's theirs. An emptied field hands control back.
  const [autoManaged, setAutoManaged] = React.useState(true);
  const [subdomainStatus, setSubdomainStatus] = React.useState<SubdomainStatus>("idle");
  const [subdomainReason, setSubdomainReason] = React.useState<string | null>(null);
  const initializedRef = React.useRef(false);
  const checkSeqRef = React.useRef(0);

  const clientLoaded = Boolean(client);
  const clientName = client ? client.name : "";
  const clientSubdomain = client ? client.subdomain : "";
  const servicesKey = client ? client.services.join(",") : "";
  // A tenant exists at this address: a database, a bucket and a URL are named
  // after it, none of which can be renamed. The server refuses the patch too;
  // this is so the screen never asks.
  const subdomainLocked = Boolean(client && client.companyId !== null);

  // On first load, a saved subdomain is treated as hand-picked and an empty one
  // as ours to fill in. The old rule — re-derive the slug and compare — needed
  // the browser to know the slug rules, which is exactly what it must not.
  React.useEffect(() => {
    if (!clientLoaded || initializedRef.current) return;
    initializedRef.current = true;
    setAutoManaged(clientSubdomain === "");
  }, [clientLoaded, clientSubdomain]);

  // Books is not optional. Older drafts (or a blank services row) get it added
  // the moment this screen sees them, so step branching and the server agree.
  React.useEffect(() => {
    if (!clientLoaded) return;
    const services = servicesKey === "" ? [] : (servicesKey.split(",") as ServiceModule[]);
    if (!services.includes("books")) {
      updateClient(clientId, { services: ["books", ...services] });
    }
  }, [clientLoaded, servicesKey, clientId]);

  // Autogeneration, debounced: the NAME goes to the server and the address
  // comes back, already checked and already free (the server hands back a
  // numbered variant when the plain slug is taken). The browser owns no slug
  // rules at all — when it had its own copy the two drifted, and the address
  // the CA was reading was not the one being saved.
  React.useEffect(() => {
    if (!clientLoaded || !initializedRef.current || !autoManaged || subdomainLocked) return;
    const seq = ++checkSeqRef.current;

    if (clientName.trim() === "") {
      setSubdomainStatus("idle");
      setSubdomainReason(null);
      return;
    }

    setSubdomainStatus("checking");
    const timer = setTimeout(() => {
      checkSubdomain(clientId, "", clientName)
        .then((result) => {
          if (seq !== checkSeqRef.current) return;
          if (!result.valid) {
            setSubdomainStatus("invalid");
            setSubdomainReason(result.reason);
            return;
          }
          setSubdomainStatus("available");
          setSubdomainReason(null);
          // Read the store rather than the closure: this effect is keyed on the
          // name, so what it captured may be a subdomain ago.
          const saved = getClient(clientId);
          if (saved && result.suggestion !== "" && saved.subdomain !== result.suggestion) {
            updateClient(clientId, { subdomain: result.suggestion });
          }
        })
        .catch(() => {
          // Can't reach the check — don't trap the CA on this screen for it.
          if (seq === checkSeqRef.current) setSubdomainStatus("error");
        });
    }, CHECK_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [clientLoaded, autoManaged, subdomainLocked, clientName, clientId]);

  // Hand-typed, debounced: the server decides whether it is legal (grammar,
  // length, and whether it is really a pasted web address) and whether it is
  // free. A refusal is rendered in the server's words and blocks Continue —
  // nothing is silently corrected under a CA who typed a specific address.
  React.useEffect(() => {
    if (!clientLoaded || !initializedRef.current || autoManaged || subdomainLocked) return;
    const seq = ++checkSeqRef.current;

    if (clientSubdomain === "") {
      setSubdomainStatus("idle");
      setSubdomainReason(null);
      return;
    }

    setSubdomainStatus("checking");
    const timer = setTimeout(() => {
      checkSubdomain(clientId, clientSubdomain)
        .then((result) => {
          if (seq !== checkSeqRef.current) return;
          if (!result.valid) {
            setSubdomainStatus("invalid");
            setSubdomainReason(result.reason);
            return;
          }
          setSubdomainStatus(result.available ? "available" : "taken");
          setSubdomainReason(null);
        })
        .catch(() => {
          if (seq === checkSeqRef.current) setSubdomainStatus("error");
        });
    }, CHECK_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [clientLoaded, autoManaged, subdomainLocked, clientSubdomain, clientId]);

  if (!client) return null;

  function toggleService(value: string) {
    if (!client || value === "books") return;
    const services = client.services.includes(value as ServiceModule)
      ? client.services.filter((service) => service !== value)
      : [...client.services, value as ServiceModule];
    updateClient(clientId, { services });
  }

  function selectCfoSource(value: string) {
    updateClient(clientId, { cfoSource: value as CfoSource });
  }

  function updateBusinessNature(patch: { description?: string; category?: BusinessNatureCategory }) {
    if (!client) return;
    updateClient(clientId, {
      businessNature: {
        description: client.businessNature ? client.businessNature.description : "",
        category: client.businessNature ? client.businessNature.category : "other",
        ...patch,
      },
    });
  }

  function onSubdomainChange(raw: string) {
    if (subdomainLocked) return;
    const value = tidySubdomainInput(raw);
    // Typing takes ownership; clearing the field hands it back to autogeneration.
    setAutoManaged(value === "");
    updateClient(clientId, { subdomain: value });
  }

  // CFO chosen without Books can no longer happen (Books is always on), but the
  // guard stays so the rule survives if that ever changes.
  const cfoOnly = client.services.includes("cfo") && !client.services.includes("books");

  const missing: string[] = [];
  if (client.name.trim() === "") missing.push("company name");
  if (client.subdomain.trim() === "") missing.push("subdomain");
  if (!client.businessNature || client.businessNature.description.trim() === "") {
    missing.push("what the client does");
  }
  if (!client.businessNature || !client.businessNature.category) missing.push("nature of business");
  if (cfoOnly && !client.cfoSource) missing.push("CFO data source");

  const subdomainBlocked =
    !subdomainLocked
    && (subdomainStatus === "invalid"
      || (client.subdomain !== "" && (subdomainStatus === "taken" || subdomainStatus === "checking")));
  const canContinue = missing.length === 0 && !subdomainBlocked;

  return (
    <SubStepShell
      title="Company details"
      description="Fill in the details below to start creating your company."
      thenWhat={
        canContinue
          ? "Next: connecting the books"
          : subdomainStatus === "invalid" && subdomainReason !== null
            ? subdomainReason
            : missing.length > 0
              ? `Still needed: ${missing.join(", ")}.`
              : "Waiting on the subdomain check…"
      }
      thenWhatBelow
      onContinue={goNext}
      continueDisabled={!canContinue}
    >
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs font-medium text-muted-foreground">
          Company Legal Name <span className="text-destructive">*</span>
        </span>
        <input
          value={client.name}
          onChange={(event) => updateClient(clientId, { name: event.target.value })}
          className="h-9 rounded-md border bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          placeholder="e.g. Meridian Exports Private Limited"
        />
        <span className="text-xs text-muted-foreground">
          Should match the name on the GST registration or PAN, not a trade name.
        </span>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs font-medium text-muted-foreground">Entity type</span>
        <select
          value={client.entityType}
          onChange={(event) => updateClient(clientId, { entityType: event.target.value as EntityType })}
          className="h-9 rounded-md border bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          {ENTITY_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs font-medium text-muted-foreground">
          Subdomain <span className="text-destructive">*</span>
        </span>
        <input
          value={client.subdomain}
          onChange={(event) => onSubdomainChange(event.target.value)}
          disabled={subdomainLocked}
          className="h-9 rounded-md border bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-70"
          placeholder={SUBDOMAIN_PLACEHOLDER}
        />
        <span className="text-xs text-muted-foreground">
          {client.subdomain || SUBDOMAIN_PLACEHOLDER}.{coTenantDomain(client.tenantDomain)} is the web address your team
          uses for this client in Zournal.
        </span>
        {subdomainLocked && (
          <span className="text-xs text-muted-foreground">Locked — the tenant is provisioned at this address.</span>
        )}
        {!subdomainLocked && client.subdomain !== "" && subdomainStatus === "checking" && (
          <span className="text-xs text-muted-foreground">Checking availability…</span>
        )}
        {!subdomainLocked && client.subdomain !== "" && subdomainStatus === "available" && (
          <span className="text-xs text-emerald-600">✓ {client.subdomain} is available.</span>
        )}
        {!subdomainLocked && client.subdomain !== "" && subdomainStatus === "taken" && (
          <span className="text-xs text-destructive">
            {client.subdomain} is already in use. Pick another, or clear the field to autogenerate one.
          </span>
        )}
        {!subdomainLocked && subdomainStatus === "invalid" && subdomainReason !== null && (
          <span className="text-xs text-destructive">{subdomainReason}</span>
        )}
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs font-medium text-muted-foreground">
          What does this client do? <span className="text-destructive">*</span>
        </span>
        <textarea
          value={client.businessNature ? client.businessNature.description : ""}
          onChange={(event) => updateBusinessNature({ description: event.target.value })}
          rows={4}
          className="min-h-24 resize-none rounded-md border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50 [field-sizing:content]"
          placeholder="e.g. Manufactures and exports cotton textiles"
        />
        <span className="text-xs text-muted-foreground">
          Describe what this client sells and where the money comes from. This shapes the ledgers and
          tax treatment we set up.
        </span>
        <span className="text-xs text-muted-foreground/70">
          Example: Sharma Textiles is a wholesale fabric trader in Coimbatore. Revenue comes from three
          streams: bulk fabric sales to garment units (about 70%), job-work processing charges (20%),
          and occasional export sales (10%). Purchases are mostly from local powerloom units, some on
          credit.
        </span>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs font-medium text-muted-foreground">
          Nature of business <span className="text-destructive">*</span>
        </span>
        <select
          value={client.businessNature ? client.businessNature.category : ""}
          onChange={(event) => updateBusinessNature({ category: event.target.value as BusinessNatureCategory })}
          className="h-9 rounded-md border bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          <option value="" disabled>
            Select one
          </option>
          {BUSINESS_NATURE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-muted-foreground">Services</span>
        <DecisionCards options={SERVICE_OPTIONS} selected={client.services} onToggle={toggleService} multiple />
      </div>

      {cfoOnly && (
        <QuestionCard
          headline="Can Zournal connect to this client's accounting system?"
          whyLine="This decides whether we read this client's books live or work from a trial balance you upload."
          options={CFO_SOURCE_OPTIONS}
          selected={client.cfoSource}
          onSelect={selectCfoSource}
        />
      )}
    </SubStepShell>
  );
}
